/**
 * CMS-14 — audit log: filters, server pagination, CSV export and a before → after diff viewer
 * (docs/06 §2.13, §4).
 */
import type { AuditEntryDTO, CmsAuditQueryInput, CmsAuditResponse } from '@akpl/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { api } from '../api';
import { DataTable, PAGE_SIZE } from '../components/data-table/DataTable';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { Drawer } from '../components/ui/drawer';
import { Input, Label, Select } from '../components/ui/field';
import { auditTone, Badge } from '../components/ui/panel';
import { useT } from '../i18n';
import type { CsvColumn } from '../lib/csv';
import { diffFields } from '../lib/diff';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatDateTime, formatStamp, formatStampSeconds, stringifyValue } from '../lib/format';

/** Date windows of the "Fecha" filter (Figma CMS-14). */
const RANGES = { all: 0, d1: 1, d7: 7, d30: 30 } as const;
type RangeKey = keyof typeof RANGES;

interface Filters {
  actor: string;
  action: string;
  entity: string;
  entityId: string;
  range: RangeKey;
}

const EMPTY: Filters = { actor: '', action: '', entity: '', entityId: '', range: 'd30' };

/**
 * What the ENTIDAD column and the row name call the entity: the title the API resolved, else
 * the kind plus the raw id (a deleted song, a `grant import:0` bulk row).
 */
export function entityName(entry: Pick<AuditEntryDTO, 'entityType' | 'entityId' | 'entityLabel'>) {
  if (entry.entityLabel) return `${entry.entityType} «${entry.entityLabel}»`;
  return entry.entityId ? `${entry.entityType} ${entry.entityId}` : entry.entityType;
}

/** `sort` values the API accepts (docs/03 §2 "Audit"). */
const SORT_IDS = new Set(['created_at', 'action', 'entity_type', 'actor']);

export function toQuery(filters: Filters, page: number, sorting: SortingState): CmsAuditQueryInput {
  const query: CmsAuditQueryInput = { page, pageSize: PAGE_SIZE };
  if (filters.actor) query.actor = filters.actor;
  if (filters.action) query.action = filters.action;
  if (filters.entity) query.entity = filters.entity;
  if (filters.entityId.trim()) query.entityId = filters.entityId.trim();
  const days = RANGES[filters.range];
  if (days > 0) query.from = new Date(Date.now() - days * 86_400_000).toISOString();
  const sort = sorting[0];
  if (sort && SORT_IDS.has(sort.id)) {
    query.sort = sort.id;
    query.dir = sort.desc ? 'desc' : 'asc';
  }
  return query;
}

/**
 * The entry detail.
 *
 * QA 2026-09-15 **UX-564**: it used to be a plain `Panel` beside the table — `role=dialog`
 * count 0, focus left on the row that opened it, Escape did nothing and it had no accessible
 * name; the only detail surface in the console that was not a drawer. It is now the shared
 * `Drawer`, which takes focus on open, traps Tab, closes on Escape or the scrim and gives focus
 * back to the row.
 */
function DiffViewer({ entry, onClose }: { entry: AuditEntryDTO; onClose: () => void }) {
  const { t, i18n } = useT();
  const fields = diffFields(entry.before, entry.after);
  const copy = () => {
    void navigator.clipboard?.writeText(
      JSON.stringify({ before: entry.before, after: entry.after }, null, 2),
    );
  };

  return (
    <Drawer
      open
      width={520}
      onClose={onClose}
      title={`${t('cms:audit.diff')} · ${entry.action}`}
      subtitle={`${entry.actorEmail ?? entry.actorUserId ?? '—'} · ${formatDateTime(
        entry.createdAt,
        i18n.language,
      )}`}
      footer={
        <>
          <Button variant="secondary" onClick={copy}>
            {t('cms:audit.copyJson')}
          </Button>
          <Button onClick={onClose}>{t('common:actions.close')}</Button>
        </>
      }
    >
      <div>
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-2 text-[12px] font-semibold tracking-wide text-muted uppercase">
          <span>{t('cms:audit.field')}</span>
          <span>{t('cms:audit.beforeAfter')}</span>
        </div>
        {fields.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-muted">{t('cms:audit.noChanges')}</p>
        ) : (
          <ul className="mt-2">
            {fields.map((field) => (
              <li
                key={field.field}
                className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-2 border-t border-line py-2.5 first:border-0"
              >
                <span className="truncate text-[13px] text-ink">{field.field}</span>
                <span className="flex flex-wrap items-center gap-1.5">
                  <code className="rounded bg-danger/10 px-1.5 py-0.5 text-[12px] text-danger-ink">
                    {stringifyValue(field.before)}
                  </code>
                  <span aria-hidden="true" className="text-muted">
                    →
                  </span>
                  <code className="rounded bg-ok/10 px-1.5 py-0.5 text-[12px] text-ok-ink">
                    {stringifyValue(field.after)}
                  </code>
                </span>
              </li>
            ))}
          </ul>
        )}

        <dl className="mt-3 rounded-lg bg-canvas px-3 py-2.5 text-[12px]">
          {(
            [
              ['ip', entry.ip],
              ['userAgent', entry.userAgent],
              ['reqId', entry.reqId],
              [
                'entity',
                // The title when the API resolved one, always over the raw id.
                `${entry.entityType}${entry.entityLabel ? ` «${entry.entityLabel}»` : ''}${
                  entry.entityId ? ` / ${entry.entityId}` : ''
                }`,
              ],
            ] as const
          ).map(([key, value]) => (
            <div key={key} className="flex gap-3 py-0.5">
              <dt className="w-24 shrink-0 text-muted">
                {t(`cms:audit.meta.${key}` as 'cms:audit.meta.ip')}
              </dt>
              <dd className="min-w-0 flex-1 break-words text-ink">{value || '—'}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Drawer>
  );
}

export function AuditLog() {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }]);
  const [selected, setSelected] = useState<AuditEntryDTO | null>(null);

  const query = toQuery(filters, page, sorting);
  const result = useQuery<CmsAuditResponse>({
    queryKey: ['cms', 'audit', query],
    queryFn: () => api.cmsAudit({ query }),
    placeholderData: keepPreviousData,
  });

  const rows = result.data?.items ?? [];

  const columns = useMemo<ColumnDef<AuditEntryDTO>[]>(
    () => [
      {
        id: 'created_at',
        header: t('cms:audit.date'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted">
            {formatStamp(row.original.createdAt, locale)}
          </span>
        ),
      },
      {
        id: 'actor',
        header: t('cms:audit.actor'),
        cell: ({ row }) => (
          <span className="truncate">
            {row.original.actorEmail ?? row.original.actorUserId ?? '—'}
          </span>
        ),
      },
      {
        id: 'action',
        header: t('cms:audit.action'),
        cell: ({ row }) => (
          <Badge tone={auditTone(row.original.action)}>{row.original.action}</Badge>
        ),
      },
      {
        id: 'entity_type',
        header: t('cms:audit.entity'),
        /**
         * QA 2026-09-15 (standing leftover): this column read `song
         * 01M2PZSBAYZ007FG40DVQ7ZHCN` — the kind plus a ULID, which is not something an operator
         * scanning the log can recognise. The API now resolves a title for the kinds that have
         * one (`entityLabel`, services/audit.ts); the id is kept underneath for the rows that do
         * not, and for copying.
         */
        cell: ({ row }) => (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-ink">{entityName(row.original)}</span>
            {row.original.entityLabel && row.original.entityId ? (
              <span className="truncate text-[12px] text-muted">
                {row.original.entityType} · {row.original.entityId}
              </span>
            ) : null}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  const csvColumns: CsvColumn<AuditEntryDTO>[] = [
    { header: 'created_at', value: (r) => r.createdAt },
    { header: 'actor', value: (r) => r.actorEmail ?? r.actorUserId ?? '' },
    { header: 'role', value: (r) => r.actorRole ?? '' },
    { header: 'action', value: (r) => r.action },
    { header: 'entity_type', value: (r) => r.entityType },
    { header: 'entity_id', value: (r) => r.entityId ?? '' },
    { header: 'entity_label', value: (r) => r.entityLabel ?? '' },
    { header: 'before', value: (r) => r.before },
    { header: 'after', value: (r) => r.after },
    { header: 'ip', value: (r) => r.ip ?? '' },
    { header: 'req_id', value: (r) => r.reqId ?? '' },
  ];

  const update = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const actions = [...new Set(rows.map((r) => r.action))].sort();
  const entities = [...new Set(rows.map((r) => r.entityType))].sort();
  const actors = [
    ...new Map(rows.map((r) => [r.actorUserId ?? '', r.actorEmail ?? r.actorUserId ?? ''])),
  ].filter(([uid]) => uid);

  return (
    <>
      <PageHeader title={t('cms:audit.title')} subtitle={t('cms:audit.subtitle')} />

      <div className="px-6 pb-8">
        <div className="min-w-0">
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            label={t('cms:audit.title')}
            total={result.data?.total ?? 0}
            page={page}
            onPageChange={setPage}
            sorting={sorting}
            onSortingChange={(next) => {
              setSorting(next);
              setPage(1);
            }}
            isLoading={result.isLoading}
            isFetching={result.isFetching}
            isError={result.isError}
            onRetry={() => void result.refetch()}
            onRowClick={setSelected}
            /**
             * QA 2026-09-15 **UX-560**: fifty rows shared eight names, the commonest repeated
             * 36 times, because the label was the action alone — arrowing down the log was 36
             * identical announcements. The entry is action + entity + time.
             *
             * **UX-575**: 46 of 50 after that, because bulk operations share the action, the
             * entity *and* the minute — three «grant.create · grant import:0 · 17/9, 02:56».
             * The time is to the second, which is what tells them apart.
             */
            rowLabel={(row) =>
              t('cms:table.openRow', {
                name: t('cms:audit.rowName', {
                  action: row.action,
                  entity: entityName(row),
                  time: formatStampSeconds(row.createdAt, locale),
                }),
              })
            }
            sortableIds={SORT_IDS}
            selectedRowId={selected?.id ?? null}
            csv={{
              filename: 'akapella-audit.csv',
              columns: csvColumns,
              // The whole filtered set, not the page on screen (CMS-22).
              rows: (onProgress) =>
                fetchAllRowsResult(
                  (exportPage, pageSize) =>
                    api.cmsAudit({ query: { ...query, page: exportPage, pageSize } }),
                  { onProgress },
                ),
            }}
            toolbar={
              <>
                <span className="flex items-center gap-1.5">
                  <Label htmlFor="f-actor">{t('cms:audit.actor')}</Label>
                  <Select
                    id="f-actor"
                    className="w-40"
                    value={filters.actor}
                    onChange={(e) => update({ actor: e.target.value })}
                  >
                    <option value="">{t('cms:audit.all')}</option>
                    {actors.map(([uid, label]) => (
                      <option key={uid} value={uid}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </span>
                <span className="flex items-center gap-1.5">
                  <Label htmlFor="f-action">{t('cms:audit.action')}</Label>
                  <Select
                    id="f-action"
                    className="w-40"
                    value={filters.action}
                    onChange={(e) => update({ action: e.target.value })}
                  >
                    <option value="">{t('cms:audit.all')}</option>
                    {actions.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </Select>
                </span>
                <span className="flex items-center gap-1.5">
                  <Label htmlFor="f-entity">{t('cms:audit.entity')}</Label>
                  <Select
                    id="f-entity"
                    className="w-36"
                    value={filters.entity}
                    onChange={(e) => update({ entity: e.target.value })}
                  >
                    <option value="">{t('cms:audit.all')}</option>
                    {entities.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </Select>
                </span>
                <span className="flex items-center gap-1.5">
                  <Label htmlFor="f-range">{t('cms:audit.date')}</Label>
                  <Select
                    id="f-range"
                    className="w-36"
                    value={filters.range}
                    onChange={(e) => update({ range: e.target.value as RangeKey })}
                  >
                    {(Object.keys(RANGES) as RangeKey[]).map((key) => (
                      <option key={key} value={key}>
                        {t(`cms:audit.range.${key}` as 'cms:audit.range.d7')}
                      </option>
                    ))}
                  </Select>
                </span>
                <Input
                  className="w-52"
                  placeholder={t('cms:audit.searchEntity')}
                  aria-label={t('cms:audit.searchEntity')}
                  value={filters.entityId}
                  onChange={(e) => update({ entityId: e.target.value })}
                />
                {/* UX-514: named like the other eight lists, not just «Limpiar». */}
                <Button variant="link" size="sm" onClick={() => update(EMPTY)}>
                  {t('cms:table.clearFilters')}
                </Button>
              </>
            }
          />
        </div>

        {selected && <DiffViewer entry={selected} onClose={() => setSelected(null)} />}
      </div>
    </>
  );
}
